import React, { useState } from 'react';
import './FAQPage.css';

const FAQPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'getting-started', name: 'Getting Started' },
    { id: 'referral-program', name: 'Referral Program' },
    { id: 'account', name: 'Account & Security' },
    { id: 'properties', name: 'Property Management' },
    { id: 'tenants', name: 'Tenant Management' },
    { id: 'payments', name: 'Payments' },
    { id: 'hoa', name: 'HOA Communities' },
    { id: 'maintenance', name: 'Maintenance' },
    { id: 'financial', name: 'Financial' },
    { id: 'troubleshooting', name: 'Troubleshooting' },
  ];

  const faqs = [
    {
      category: 'getting-started',
      question: 'What is EstateFlow?',
      answer: 'EstateFlow is an all-in-one property management platform designed for landlords, property managers, and HOA communities worldwide. With a special focus on the Caribbean, we also serve property owners across the USA, Canada, and 40+ countries. We help you automate rent collection, manage tenants, track maintenance, and handle payouts - all from one easy-to-use dashboard.'
    },
    {
      category: 'getting-started',
      question: 'How do I sign up for EstateFlow?',
      answer: '1. Visit EstateFlow.com\n2. Click "Sign Up" in the top right corner\n3. Choose your account type (Landlord, Property Manager, or HOA Community)\n4. Enter your email address and create a secure password\n5. Verify your email by clicking the link we send you\n6. Complete your profile with business information\n7. Add your first property to get started!'
    },
    {
      category: 'account',
      question: 'How do I reset my password?',
      answer: '1. Click "Forgot Password?" on the login page\n2. Enter your registered email address\n3. Check your email for a password reset link\n4. Click the link (valid for 24 hours)\n5. Enter your new password (minimum 8 characters)\n6. Click "Save" and log in with your new password'
    },
    {
      category: 'account',
      question: 'How secure is EstateFlow?',
      answer: 'We take security seriously with:\n• 256-bit SSL Encryption - All data transmitted is encrypted\n• Secure Payment Processing - PCI-DSS compliant payment partners\n• Regular Security Audits - Third-party penetration testing\n• Data Backup - Daily automated backups\n• Two-Factor Authentication - Optional extra security layer\n• Role-Based Access - Users only see what they need'
    },
    {
      category: 'properties',
      question: 'How do I add a new property?',
      answer: '1. Go to Properties in the left sidebar\n2. Click "+ Add Property" button\n3. Enter property details (name, address, type, units)\n4. Upload property photo (optional)\n5. Click "Save Property"\n\nYour property is now added and ready for tenant assignments!'
    },
    {
      category: 'properties',
      question: 'Can I manage multiple properties?',
      answer: 'Yes! EstateFlow supports unlimited properties. Your subscription pricing is based on the total number of units across all properties. Use the property dropdown in the top navigation bar to switch between properties.'
    },
    {
      category: 'tenants',
      question: 'How do I invite a new tenant?',
      answer: '1. Go to Tenants in the sidebar\n2. Click "+ Invite Tenant" button\n3. Enter tenant information (name, email, phone)\n4. Assign them to a property and unit\n5. Set move-in date and lease terms\n6. Click "Send Invitation"\n\nThe tenant receives an email with instructions to create their account.'
    },
    {
      category: 'tenants',
      question: 'How do tenants access their portal?',
      answer: 'Tenants log in at EstateFlow.com/login using the email address you used in their invitation and the password they created. In their portal, tenants can: pay rent online, view payment history, submit maintenance requests, view lease documents, and message the landlord.'
    },
    {
      category: 'payments',
      question: 'How do tenants pay rent?',
      answer: 'Tenants have multiple payment options:\n• Credit/Debit Card - Instant processing\n• Bank Transfer - Direct from their bank account\n\nPayment process:\n1. Tenant logs into their portal\n2. Clicks "Pay Rent" button\n3. Selects payment method\n4. Reviews payment amount\n5. Confirms payment\n6. Receives instant confirmation receipt'
    },
    {
      category: 'payments',
      question: 'When do I receive payments?',
      answer: 'Standard Processing:\n• Credit card payments: 2-3 business days\n• Bank transfers: 3-5 business days\n• Funds deposited directly to your linked bank account\n\nInstant Payouts (Premium feature):\n• Receive funds within hours\n• Small fee applies (1.5%)\n• Available for qualifying accounts'
    },
    {
      category: 'payments',
      question: 'How much do payment processing fees cost?',
      answer: 'Estate Flow Platform Fee: 5% per transaction\n\nThis includes:\n• Payment processing (credit cards, ACH transfers)\n• Automated receipts and notifications\n• Bank deposit processing\n• Platform maintenance and support\n\nThe 5% fee is automatically deducted from each payment before it reaches your account. For example, on a $1,000 rent payment, $50 goes to Estate Flow and $950 is deposited to your account.'
    },
    {
      category: 'hoa',
      question: 'How do I set up an HOA community?',
      answer: '1. Sign up and select "HOA Community" as account type\n2. Enter community information (name, address, number of units)\n3. Set HOA dues amount and frequency (monthly, quarterly, annually)\n4. Add HOA board members\n5. Configure common areas\n6. Invite homeowners'
    },
    {
      category: 'hoa',
      question: 'How do homeowners pay HOA dues?',
      answer: 'HOA owners pay through their resident portal by logging in, clicking "Pay Dues", selecting payment method, reviewing amount, and submitting payment. Automatic reminders ensure timely payments!'
    },
    {
      category: 'maintenance',
      question: 'How do tenants submit maintenance requests?',
      answer: '1. Tenant logs into their portal\n2. Clicks "Submit Request" under Maintenance\n3. Fills out the form (category, description, priority)\n4. Uploads photos (optional)\n5. Clicks "Submit Request"\n\nLandlord receives instant notification!'
    },
    {
      category: 'maintenance',
      question: 'How do I respond to maintenance requests?',
      answer: '1. Go to Maintenance in your dashboard\n2. View "Open Requests" tab\n3. Click on the request to view details\n4. Take action:\n   • Mark as "In Progress"\n   • Assign to Vendor\n   • Add Notes\n   • Close Request when completed\n\nTenant receives automatic status updates.'
    },
    {
      category: 'financial',
      question: 'How do I request a payout?',
      answer: '1. Go to Wallet or Payouts in your dashboard\n2. View your available balance\n3. Click "Request Payout"\n4. Enter payout amount\n5. Select bank account (or add new)\n6. Confirm transaction\n7. Funds transfer within 2-5 business days'
    },
    {
      category: 'financial',
      question: 'What are the payout fees?',
      answer: 'Standard Payouts: Free (2-5 business days)\nInstant Payouts: 1.5% fee (funds within hours)\n\nMinimum payout amount: $10.00'
    },
    {
      category: 'troubleshooting',
      question: "I can't log in to my account",
      answer: 'Solutions:\n1. Check your email and password (ensure caps lock is off)\n2. Reset your password by clicking "Forgot Password?"\n3. Clear browser cache (try incognito window)\n4. Check your email for verification\n5. Try a different browser\n\nStill can\'t log in? Contact support with your registered email address.'
    },
    {
      category: 'troubleshooting',
      question: 'Payment is not showing up',
      answer: 'Check these first:\n1. Processing time: Payments take 2-3 business days\n2. Bank account: Verify correct bank account is linked\n3. Transaction status: Check if payment is "Pending"\n4. Email confirmation: Look for payment confirmation email\n\nIf payment is delayed beyond 5 business days, contact support with transaction ID.'
    },
    {
      category: 'referral-program',
      question: 'What is the Estate Flow Referral Program?',
      answer: 'The Estate Flow Referral Program allows ANYONE - landlords, property managers, real estate agents, or individuals - to earn 25% recurring commission by referring new users to Estate Flow. When someone signs up using your unique referral link, you earn money every month they stay subscribed. It\'s a win-win: you help others discover a great platform while earning passive income!'
    },
    {
      category: 'referral-program',
      question: 'Who can become a referral agent?',
      answer: 'EVERYONE is welcome! You can be:\n• Landlords with multiple properties\n• Real Estate Agents and Brokers\n• Property Managers\n• HOA Board Members\n• Friends or family members\n• Social media influencers\n• Content creators\n• Any individual who wants to earn extra income\n\nNo special qualifications needed - if you know property owners or managers who could benefit from Estate Flow, you can become a referral agent!'
    },
    {
      category: 'referral-program',
      question: 'How do I become a referral agent?',
      answer: '1. Sign up for an Estate Flow account (any role)\n2. Log in to your dashboard\n3. Click on the "Referrals" tab\n4. Click "Get My Referral Link"\n5. You\'ll instantly receive:\n   • A unique referral code (e.g., JOHN-ABC123)\n   • A shareable link (e.g., apcestateflow.com/signup?ref=YOUR-CODE)\n6. Start sharing your link and earning commissions!\n\nIt takes less than 2 minutes to get started!'
    },
    {
      category: 'referral-program',
      question: 'How much commission do I earn?',
      answer: '25% RECURRING COMMISSION on every referral - FOR LIFE!\n\nExample Earnings:\n• 1 landlord paying $99/month = $24.75/month for you\n• 5 landlords = $123.75/month\n• 10 landlords = $247.50/month\n• 20 landlords = $495.00/month\n\nThis is RECURRING income - you earn every single month as long as your referral stays subscribed. If they stay for a year, you earn 12 months of commissions!'
    },
    {
      category: 'referral-program',
      question: 'When and how do I get paid?',
      answer: 'Payout Details:\n• Minimum Payout: $70.00\n• Processing Time: 3-5 business days\n• Payment Method: Direct deposit to your account\n• Request Payouts: Anytime from your Referral Dashboard\n\nYou can track your earnings in real-time from the Referrals tab. When you reach $70, simply click "Request Payout" and we\'ll process your payment within 3-5 business days.'
    },
    {
      category: 'referral-program',
      question: 'How do I share my referral link?',
      answer: 'Share your link anywhere:\n\n1. Social Media: Post on Facebook, Instagram, LinkedIn, Twitter\n2. Email: Send to property owners you know\n3. WhatsApp: Share in landlord groups\n4. Website/Blog: Add your link to property-related content\n5. Word of Mouth: Tell friends, family, colleagues\n6. Business Cards: Add your referral code\n7. Real Estate Listings: Include in property descriptions\n\nYour Referral Dashboard has one-click share buttons for all major platforms!'
    },
    {
      category: 'referral-program',
      question: 'Can I refer myself or my own business?',
      answer: 'No, you cannot refer yourself. The referral program is designed for you to refer OTHER property owners, managers, or HOA communities. Self-referrals are not eligible for commissions.\n\nHowever, you CAN:\n• Refer your business partner (separate entity)\n• Refer properties you manage for clients\n• Refer fellow landlords in your network'
    },
    {
      category: 'referral-program',
      question: 'How do I track my referrals and earnings?',
      answer: 'Your Referral Dashboard shows everything:\n\n📊 Real-Time Stats:\n• Total Signups\n• Active Referrals\n• Pending Payout\n• Lifetime Earnings\n\n📈 Earnings Chart:\n• Monthly earnings breakdown for the last 12 months\n• Visual progress tracking\n\n🏆 Leaderboard:\n• See how you rank against other referral agents\n• Competitive rankings and rewards\n\n📧 Email Notifications:\n• Get instant alerts when someone signs up with your code\n• Track new commissions in real-time\n\nAccess your dashboard anytime from the "Referrals" tab!'
    },
    {
      category: 'referral-program',
      question: 'What if my referral cancels their subscription?',
      answer: 'If your referral cancels:\n• You stop earning commissions for that referral\n• You keep all commissions earned up to that point\n• Your lifetime earnings remain unchanged\n• You can still earn from other active referrals\n\nFocus on referring quality users who will benefit long-term from Estate Flow - this maximizes your recurring income!'
    },
    {
      category: 'referral-program',
      question: 'Is there a limit to how many people I can refer?',
      answer: 'NO LIMIT! Refer as many people as you want:\n• Unlimited signups\n• Unlimited earning potential\n• No cap on commissions\n• No expiration on your referral link\n\nThe more you share, the more you earn. Some of our top referral agents earn $1,000+ per month by referring 40+ landlords!'
    },
    {
      category: 'referral-program',
      question: 'Do I need to be a paying customer to be a referral agent?',
      answer: 'Yes, you need an Estate Flow account to access the Referral Dashboard and generate your unique referral code. However:\n\n• You can sign up for any plan (Landlord, Property Manager, HOA, or even Tenant)\n• Beta testers with free accounts can also participate\n• The value of being a referral agent far exceeds any subscription cost\n\nExample: If you refer just 4 landlords, your commission ($99/month) covers your own subscription and more!'
    },
    {
      category: 'referral-program',
      question: 'Can real estate agents and individuals really join?',
      answer: 'YES! This is a UNIVERSAL referral program open to:\n\n✅ Real Estate Agents & Brokers:\n• Perfect for agents with landlord clients\n• Add value by recommending Estate Flow\n• Earn passive income from your network\n\n✅ Individuals (Anyone!):\n• Stay-at-home parents\n• Retirees looking for extra income\n• Students\n• Social media enthusiasts\n• Anyone with a network\n\nNo property ownership required. No real estate license required. Just share your link and earn!'
    },
    {
      category: 'referral-program',
      question: 'How do I maximize my referral earnings?',
      answer: 'Top Strategies from Successful Referral Agents:\n\n1. Target High-Value Referrals:\n   • Focus on landlords with multiple properties\n   • Reach out to property management companies\n   • Connect with HOA communities\n\n2. Use Multiple Channels:\n   • Post regularly on social media\n   • Join landlord Facebook groups\n   • Attend real estate networking events\n\n3. Create Content:\n   • Write blog posts about property management\n   • Make YouTube videos reviewing Estate Flow\n   • Share success stories\n\n4. Be Genuine:\n   • Only recommend Estate Flow if it truly helps\n   • Explain real benefits, not just commissions\n   • Build trust with your audience\n\n5. Stay Consistent:\n   • Share your link weekly\n   • Follow up with interested prospects\n   • Track what works and do more of it'
    },
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleQuestion = (index) => {
    setExpandedQuestion(expandedQuestion === index ? null : index);
  };

  return (
    <div className="faq-page">
      <div className="faq-header">
        <h1>Frequently Asked Questions</h1>
        <p>Find answers to common questions about EstateFlow</p>
      </div>

      <div className="faq-search">
        <input
          type="text"
          placeholder="Search for answers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="faq-categories">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>

      <div className="faq-list">
        {filteredFAQs.length === 0 ? (
          <p className="no-results">No FAQs found matching your search.</p>
        ) : (
          filteredFAQs.map((faq, index) => (
            <div key={index} className="faq-item">
              <div
                className="faq-question"
                onClick={() => toggleQuestion(index)}
              >
                <h3>{faq.question}</h3>
                <span className="toggle-icon">
                  {expandedQuestion === index ? '−' : '+'}
                </span>
              </div>
              {expandedQuestion === index && (
                <div className="faq-answer">
                  {faq.answer.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="faq-contact">
        <h2>Still have questions?</h2>
        <p>Can't find what you're looking for? Contact our support team.</p>
        <button className="contact-btn">Contact Support</button>
      </div>
    </div>
  );
};

export default FAQPage;
